import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  useEventTracker,
  useLoggedInUser,
  useSelectedPharmacy,
  useSelectedPharmacyTimezone,
} from 'app/hooks';
import { CALENDAR_EVENTS } from 'app/tracking';

import { useCalendarPrintContext } from '../CalendarPrintContext';
import { loadAllRows } from '../helpers/loadAllRows';
import { buildPrintDocument } from '../helpers/buildPrintDocument';

export interface CalendarPrintControls {
  print: () => void;
  isPreparing: boolean;
  canPrint: boolean;
}

/**
 * The Print button's behaviour: load every row for the registered source, build the sheet and hand
 * it to CalendarPrintSheet. A mutation rather than a query because it is a user action with side
 * effects, and the global MutationCache surfaces a failed load.
 */
export default function useCalendarPrint(): CalendarPrintControls {
  const { source, setJob } = useCalendarPrintContext();
  const selectedPharmacy = useSelectedPharmacy();
  const pharmacyTimezone = useSelectedPharmacyTimezone();
  const user = useLoggedInUser();
  const trackEvent = useEventTracker();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (!source) return;

      const { rows, total, truncated } = await loadAllRows(source.fetchPage);

      if (rows.length === 0) {
        // Not an error: the pre-check is best effort (see CalendarPrintSource.isEmpty).
        enqueueSnackbar(`Nothing to print for ${source.title}`, { variant: 'info' });
        return;
      }

      const printDocument = buildPrintDocument({
        source,
        rows,
        total,
        truncated,
        pharmacyName: selectedPharmacy?.pharmacy_name ?? '',
        pharmacyTimezone,
        generatedBy: user ? `${user.forename ?? ''} ${user.surname ?? ''}`.trim() : '',
        now: new Date(),
      });

      trackEvent(CALENDAR_EVENTS.PRINTED.action, {
        ...CALENDAR_EVENTS.PRINTED,
        section: source.tab,
        source_tab: source.tab,
        row_count: printDocument.printed,
        truncated: printDocument.truncated,
      });

      setJob(printDocument);
    },
    meta: {
      errorMessage: 'Could not load the appointments to print',
    },
  });

  return {
    print: () => mutate(),
    isPreparing: isPending,
    canPrint: source !== null && !source.isEmpty,
  };
}
